<script setup lang="ts">
import type { LoginRequest } from '@/js/account/shared/model';
import { computed, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import signInImage from '@/images/account/sign-in.jpg';
import { AuthRouteName } from '@/js/account/app/router/route-names';
import AuthTitle from '@/js/account/components/auth/AuthTitle.vue';
import BaseButton from '@/js/account/components/base/BaseButton.vue';
import BaseField from '@/js/account/components/base/BaseField.vue';
import { navigateAfterAuth, signIn } from '@/js/account/entities/auth';
import AuthLayout from '@/js/account/layouts/auth-layout/AuthLayout.vue';
import { useFormErrors } from '@/js/account/shared/lib';
import { classNames } from '@/js/lib/bem';

const router = useRouter();
const route = useRoute();
const {
  clearErrors,
  clearFieldOnInput,
  setFromAxiosError,
  setGeneralError,
  fieldError,
  hasFieldError,
  generalError,
} = useFormErrors();

const hasRegisterRoute = computed(() =>
  router.hasRoute(AuthRouteName.Register),
);
const hasForgotPasswordRoute = computed(() =>
  router.hasRoute(AuthRouteName.ForgotPassword),
);

const cn = classNames('login-page');

const form = reactive<LoginRequest>({
  email: '',
  password: '',
});
const submitting = ref(false);

async function onSubmit(): Promise<void> {
  clearErrors();
  submitting.value = true;

  try {
    await signIn(form);

    const result = await navigateAfterAuth(router, route);

    if (result === 'session-unavailable') {
      setGeneralError(
        'Could not load your session. Please try again.',
      );
    }
  }
  catch (error) {
    setFromAxiosError(error);
  }
  finally {
    submitting.value = false;
  }
}
</script>

<template>
  <AuthLayout
    :src="signInImage"
    alt=""
  >
    <div :class="cn()">
      <AuthTitle :class="cn('title')" title="Sign in">
        <template #description>
          <p
            v-if="hasRegisterRoute"
            :class="cn('description')"
          >
            Is this your first time here?
            <RouterLink
              :to="{ name: AuthRouteName.Register }"
              :class="cn('link')"
            >
              Sign up
            </RouterLink>
          </p>
        </template>
      </AuthTitle>

      <form :class="cn('form')" @submit.prevent="onSubmit">
        <BaseField
          v-model="form.email"
          :class="cn('field')"
          label="Email"
          name="email"
          type="email"
          autocomplete="email"
          :invalid="hasFieldError('email') || !!generalError"
          :message="
            fieldError('email') || (generalError ? generalError : undefined)
          "
          required
          @update:model-value="clearFieldOnInput('email', { clearGeneral: true })"
        />
        <BaseField
          v-model="form.password"
          :class="cn('field')"
          label="Password"
          name="password"
          type="password"
          autocomplete="current-password"
          :invalid="hasFieldError('password') || !!generalError"
          :message="fieldError('password')"
          required
          @update:model-value="
            clearFieldOnInput('password', { clearGeneral: true })
          "
        />

        <BaseButton
          :class="cn('button')"
          type="submit"
          full-width
          :loading="submitting"
        >
          Sign in
        </BaseButton>

        <RouterLink
          v-if="hasForgotPasswordRoute"
          :to="{ name: AuthRouteName.ForgotPassword }"
          :class="cn('link')"
        >
          Forgot your password?
        </RouterLink>
      </form>
    </div>
  </AuthLayout>
</template>

<style lang="scss" scoped>
.login-page {
  max-width: 363px;
  width: 100%;

  &__form {
    display: flex;
    flex-direction: column;
  }

  &__field {
    margin-bottom: 24px;
  }

  &__button {
    margin-top: 8px;
    margin-bottom: 40px;
  }

  &__title {
    text-align: center;
  }

  &__description {
    text-align: center;
    font-family: var(--font-family-sans);
    font-weight: var(--font-weight-regular);
    font-size: 16px;
    line-height: 1.125;
    color: var(--color-text-secondary);
    margin: 0;
  }

  &__link {
    color: var(--color-primary);
    text-decoration: none;
    font-weight: var(--font-weight-regular);
    text-align: center;
    font-family: var(--font-family-sans);
    font-size: 16px;
    line-height: 1.125;
  }
}
</style>
